import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import GenericDateTimePicker from '../../../../components/GenericDateTimePicker'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { notification } from '../../../../components/Notifications'
import { type ISection } from '../../../../models/TermsSection'
import { formatDateByUserTimeZone } from '../../../../helpers/formatDateByUserTimeZone'
import { anHourAwayMessage, isAtLeastAnHourAway } from '../reopenDateRules'
import { LOCAL_TIMEZONE_NOTE, formatReopenDateError, readReopenDateError } from '../reopenDateError'

interface ReopenSurveyModalProps {
  selectedRow?: ISection | null
  handleCloseModal?: () => void
  onSubmit?: (payload: ReopenSurveyPayload) => Promise<void> | void
  isSubmitting?: boolean
}

export interface ReopenSurveyPayload {
  slotId: string
  sectionId: string
  endTimestamp: string
  earlyAccessReleaseTimestamp1: string
  earlyAccessReleaseTimestamp2: string

}

interface FormValues {
  endTimestamp: Date | null
  earlyAccessReleaseTimestamp1: Date | null
  earlyAccessReleaseTimestamp2: Date | null
}

const validationSchema: yup.ObjectSchema<FormValues> = yup.object({
  endTimestamp: yup
    .date()
    .typeError('End date is required')
    .test('end-an-hour-away', anHourAwayMessage('End date'), isAtLeastAnHourAway)
    .nullable()
    .required('End date is required'),

  earlyAccessReleaseTimestamp1: yup
    .date()
    .typeError('Early access release date is required')
    .test('admin-release-an-hour-away', anHourAwayMessage('Early access release date'), isAtLeastAnHourAway)
    .nullable(),
  earlyAccessReleaseTimestamp2: yup
    .date()
    .typeError('Early access release date is required')
    .test('instructor-release-an-hour-away', anHourAwayMessage('Early access release date'), isAtLeastAnHourAway)
    .nullable()
}) as yup.ObjectSchema<FormValues>

const getClampedDate = (timestamp?: string | null): Date | null => {
  if (!timestamp) return new Date()
  const parsedDate = new Date(timestamp)
  const now = new Date()
  if (isNaN(parsedDate.getTime()) || parsedDate < now) {
    return now
  }
  return parsedDate
}

const ReopenSurveyModal: React.FC<ReopenSurveyModalProps> = ({
  selectedRow,
  handleCloseModal = () => { },
  onSubmit,
  isSubmitting = false
}) => {
  const [formError, setFormError] = useState<string | null>(null)
  const methods = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      endTimestamp: getClampedDate(selectedRow?.endTimestamp),
      earlyAccessReleaseTimestamp1: selectedRow?.earlyAccessReleaseTimestamp_1 ? new Date(selectedRow.earlyAccessReleaseTimestamp_1) : null,
      earlyAccessReleaseTimestamp2: selectedRow?.earlyAccessReleaseTimestamp_2 ? new Date(selectedRow.earlyAccessReleaseTimestamp_2) : null

    }
  })

  const { handleSubmit, reset } = methods

  const handleFormSubmit = async (values: FormValues): Promise<void> => {
    if (!selectedRow?.linkedSection?.sectionId || !selectedRow?.slotId) {
      notification('Unable to determine section details. Please try again.', { variant: 'error' })
      return
    }

    if (!values.endTimestamp) {
      setFormError('End date is required')
      return
    }

    try {
      setFormError(null)
      const payload: ReopenSurveyPayload = {
        slotId: selectedRow.slotId,
        sectionId: selectedRow.linkedSection.sectionId,
        // GenericDateTimePicker already stores each field as the true UTC instant
        // of the picked time, so serialize it directly — matching SurveyProjectReopenModal.
        // The previous getTimezoneOffset() subtraction double-shifted the value by the
        // browser's offset, sending the local wall-clock time mislabeled as UTC.
        earlyAccessReleaseTimestamp1: values.earlyAccessReleaseTimestamp1
          ? values.earlyAccessReleaseTimestamp1.toISOString()
          : '',
        earlyAccessReleaseTimestamp2: values.earlyAccessReleaseTimestamp2
          ? values.earlyAccessReleaseTimestamp2.toISOString()
          : '',
        endTimestamp: values.endTimestamp
          ? values.endTimestamp.toISOString()
          : ''
      }
      await onSubmit?.(payload)
      handleCloseModal()
    } catch (error: any) {
      // The reopen endpoint returns the offending boundary dates as raw UTC fields,
      // varying by which rule failed (24h / one-hour-future / project-end). Render
      // whichever are present in the viewer's local timezone so the message matches
      // the dates they picked rather than showing raw UTC.
      // The lead sentence comes from the backend's `end_timestamp` field so its
      // wording stays single-sourced; the interceptor doesn't surface that key via
      // error.message, so read it from the raw 422 body.
      //
      // NOTE: the backend sends snake_case keys (`end_timestamp`,
      // `current_end_timestamp`, ...), but axios-case-converter's `transformResponse`
      // runs on error bodies too (axios applies it in onAdapterRejection), so by the
      // time we get here the keys are camelCased. Read the camelCase form.
      const data = error?.data
      // `endTimestamp` is a plain string today, but the backend contract notes the
      // FE interceptor is assumed to array-wrap field messages — accept either shape.
      // One shared reader, because the same payload now arrives on three paths and they were
      // drifting: this 422, the bulk endpoint's pre-flight 400, and the task result's per-section
      // detail. See `reopenDateError.ts`.
      const dateError = readReopenDateError(data)
      const baseMessage = dateError?.message ??
        (typeof error?.message === 'string' && error.message.trim().length
          ? error.message
          : 'Failed to update survey end date.')
      const detailLines = dateError?.detailLines ?? []

      setFormError(dateError !== null ? formatReopenDateError(dateError) : baseMessage)

      // Toast: lead sentence as the title, with the boundary dates stacked beneath it
      // one per line (via the notification subTitle), so it reads the same way as the
      // inline error instead of one long line. Matches the success path, which snackbars.
      notification(baseMessage, {
        variant: 'error',
        subTitle: detailLines.length
          ? (
            <>
              {detailLines.map(line => (
                <Typography key={line} sx={{ fontSize: '14px' }}>{line}</Typography>
              ))}
              {/* The same note the inline error carries. Without it the toast keeps the ambiguity
                  this change removed everywhere else: these dates are local, the sentence above
                  them names the same instant in UTC. */}
              <Typography sx={{ fontSize: '14px' }}>{LOCAL_TIMEZONE_NOTE}</Typography>
            </>
            )
          : undefined
      })
    }
  }

  return (
    <FormProvider {...methods}>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Box display='flex' flexDirection='column' gap={4}>
          <Box>
            <Typography fontSize='14px' color='text.secondary'>
            Re-opening this course section survey may require pushing back the admin and instructor results release dates. If the release dates are delayed, instructor and admin access to results will be temporarily hidden until the new release dates are reached.
            </Typography>
          </Box>
          <GenericDateTimePicker name='endTimestamp' label='New End Date' required minDate={new Date()} />
          <GenericDateTimePicker name='earlyAccessReleaseTimestamp1' label='New Admin Results Release Date' minDate={new Date()} />
          <GenericDateTimePicker name='earlyAccessReleaseTimestamp2' label='New Instructor Results Release Date' minDate={new Date()} />
          {formError && (
            <Typography fontSize='14px' color='error' role='alert' sx={{ whiteSpace: 'pre-line' }}>
              {formError}
            </Typography>
          )}
        </Box>
        <DefaultModalActions
          confirmButtonProps={{
            text: 'Save',
            type: 'submit',
            loading: isSubmitting
          }}
          destructiveButtonProps={{
            onClick: handleCloseModal
          }}
        />
      </form>
    </FormProvider>
  )
}

export default ReopenSurveyModal
